import { ART_PAL, fade, mix, n, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Neon Arcade scene behind the game zone: an 80s arcade hall at night (a pastel dusk in light) — rows of upright
 * cabinets with glowing marquees along both walls, wall neon signs (a lightning bolt, an arcade UFO, a cassette) drawn
 * as tubes, pixel twinkles and a synthwave grid on the floor at the sides. The centre stays calm (the machine sits
 * there): only the wall and a soft purple pool of light. Motifs are marked `data-motif="<x>"` (tests). No filters, no
 * text: a static image, cheap to paint.
 */
export function neonArcadeScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sn${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const floor = wide ? 720 : 680;

  const wall = P.dark ? mix(P.bg, P.purple, 0.16) : mix(P.purple, '#FFFFFF', 0.88);
  const wallLow = P.dark ? mix(P.bg, P.pink, 0.1) : mix(P.pink, '#FFFFFF', 0.88);
  const floorC = P.dark ? mix(P.bg, P.blue, 0.12) : mix(P.sky, '#FFFFFF', 0.86);
  const cab = P.dark ? mix(P.bg, P.purple, 0.32) : mix(P.purple, '#FFFFFF', 0.6);
  const screen = P.dark ? shade(P.bg, 0.2) : mix(P.purple, P.ink, 0.6);
  const neonC = (c: string) => (P.dark ? tint(c, 0.12) : shade(c, 0.05));
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  /** neon tube along `d` (halo + tube), no filters */
  const tube = (d: string, c: string, w: number) =>
    `<path d="${d}" fill="none" stroke="${neonC(c)}" stroke-opacity="${o(0.25, 0.18)}" stroke-width="${n(w * 3)}" stroke-linejoin="round" stroke-linecap="round"/>` +
    `<path d="${d}" fill="none" stroke="${neonC(c)}" stroke-opacity="${o(0.9, 0.75)}" stroke-width="${w}" stroke-linejoin="round" stroke-linecap="round"/>`;

  /** an upright cabinet standing on the floor at x (its centre), scale s, marquee of colour c */
  const cabinet = (x: number, s: number, c: string) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${floor}) scale(${s})">` +
    `<path d="M-46 0V-150L-54 -168V-232Q-54 -240 -46 -240H46Q54 -240 54 -232V-168L46 -150V0Z" fill="${cab}"/>` +
    `<rect x="-42" y="-232" width="84" height="22" rx="4" fill="${screen}"/>` +
    tube('M-38 -228H38V-214H-38Z', c, 2.4) +
    `<rect x="-36" y="-196" width="72" height="44" rx="5" fill="${screen}"/>` +
    `<rect x="-30" y="-190" width="60" height="32" rx="3" fill="${neonC(c)}" fill-opacity="${o(0.22, 0.3)}"/>` +
    `<path d="M-46 -150H46L54 -128H-54Z" fill="${shade(cab, 0.2)}"/>` +
    `<circle cx="-22" cy="-140" r="4" fill="${neonC(P.red)}"/><circle cx="16" cy="-139" r="3" fill="${neonC(P.teal)}"/>` +
    tube('M-46 0V-150L-54 -168V-232Q-54 -240 -46 -240H46Q54 -240 54 -232V-168L46 -150V0', P.purple, 1.6) +
    `</g>`;

  /** pixel twinkle */
  const twinkle = (x: number, y: number, c: string, s: number) =>
    `<path data-motif="${Math.round(x)}" transform="translate(${x} ${y})" d="M-${s / 2} -${s * 2.5}h${s}v${s * 2}h${s * 2}v${s}h-${s * 2}v${s * 2}h-${s}v-${s * 2}h-${s * 2}v-${s}h${s * 2}Z" fill="${neonC(c)}" fill-opacity="${o(0.8, 0.7)}"/>`;

  /** wall neon signs as tubes (no text) */
  const bolt = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})">${tube('M8 -40L-16 2H0L-8 40L20 -6H4L12 -40Z', P.gold, 3)}</g>`;
  const ufo = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})">` +
    tube('M-14 -4C-14 -18 -6 -24 0 -24C6 -24 14 -18 14 -4', P.sky, 3) +
    tube('M-40 0C-40 -6 -22 -10 0 -10C22 -10 40 -6 40 0C40 6 22 10 0 10C-22 10 -40 6 -40 0Z', P.pink, 3) +
    `</g>`;
  const cassette = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})">` +
    tube('M-36 -22H36V22H-36Z', P.teal, 3) +
    tube('M-14 -2a6 6 0 1 0 0.1 0M14 -2a6 6 0 1 0 0.1 0', P.teal, 2.4) +
    `</g>`;

  /** synthwave floor grid between x0 and x1, rays to (vx, floor) */
  const grid = (x0: number, x1: number, vx: number) => {
    let d = '';
    for (let i = 1; i <= 5; i++) d += `M${x0} ${n(floor + i * i * 6 + i * 4)}H${x1}`;
    for (let x = x0; x <= x1; x += wide ? 80 : 60) d += `M${n(vx + (x - vx) * 0.25)} ${floor}L${x} ${H}`;
    return `<path d="${d}" fill="none" stroke="${neonC(P.purple)}" stroke-opacity="${o(0.45, 0.4)}" stroke-width="1.6"/>`;
  };

  const defs =
    `<defs>${vgrad(`${p}wl`, wall, wallLow, wallLow)}` +
    fade(`${p}calm`, P.dark ? mix(P.bg, P.purple, 0.3) : '#FFFFFF', P.dark ? 0.4 : 0.6) +
    `</defs>`;

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}wl)"/>` +
    `<rect y="${floor}" width="${W}" height="${H - floor}" fill="${floorC}"/>` +
    `<path d="M0 ${floor}H${W}" stroke="${neonC(P.pink)}" stroke-opacity="${o(0.5, 0.45)}" stroke-width="3"/>` +
    // the calm centre: a soft pool of light behind the machine
    `<ellipse cx="${W / 2}" cy="${H * 0.5}" rx="${n(W * 0.34)}" ry="${n(H * 0.44)}" fill="url(#${p}calm)"/>`;

  if (wide) {
    art +=
      grid(0, 440, 480) +
      grid(1160, 1600, 1120) +
      cabinet(90, 1.25, P.pink) +
      cabinet(250, 1.15, P.teal) +
      cabinet(395, 1.05, P.gold) +
      cabinet(1210, 1.05, P.sky) +
      cabinet(1355, 1.15, P.pink) +
      cabinet(1515, 1.25, P.purple) +
      // signs in the side bands that stay visible next to the cabinet (desktop 1440: x ≈ 190–300 and 1330–1410)
      bolt(250, 430, 1.1) +
      cassette(1378, 430, 0.8) +
      // far corners: only on wide screens
      ufo(110, 150, 1.3) +
      bolt(1500, 170, 1.2) +
      twinkle(60, 300, P.sky, 4) +
      twinkle(215, 230, P.gold, 3) +
      twinkle(280, 300, P.pink, 3) +
      twinkle(1350, 300, P.teal, 3) +
      twinkle(1395, 230, P.gold, 3) +
      twinkle(1560, 330, P.pink, 3);
  } else {
    art += grid(0, 400, 200) + cabinet(30, 0.9, P.pink) + cabinet(370, 0.9, P.teal) + bolt(24, 470, 0.7);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Neon Arcade: a pink neon line with a tiny pixel star (repeat-x tile, 64 × 12). */
export function neonArcadeAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const pink = P.dark ? tint(P.pink, 0.1) : P.pink;
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.4);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 12" width="64" height="12">` +
    `<path d="M0 2H64" stroke="${pink}" stroke-opacity=".7" stroke-width="1.4"/>` +
    `<path d="M31 4h2v2h2v2h-2v2h-2v-2h-2v-2h2Z" fill="${gold}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'neon-arcade', scene: neonArcadeScene, accent: neonArcadeAccent });
